import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  MapPin, 
  Award, 
  ExternalLink,
  Clock,
  TrendingUp,
  Sparkles,
  ChevronDown,
  Filter,
  Search
} from 'lucide-react';
import './Exhibition.css';

const API_URL = 'https://my-musing-backend.onrender.com/api';

const Exhibition = () => {
  const [exhibitions, setExhibitions] = useState([]);
  const [displayedExhibitions, setDisplayedExhibitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsToShow, setItemsToShow] = useState(10);
  const [showMoreLoading, setShowMoreLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, recent, past
  const [searchQuery, setSearchQuery] = useState('');
  
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024
  });

  useEffect(() => {
    let resizeTimeout;
    const updateViewport = () => {
      const width = window.innerWidth;
      setViewport({
        width,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024
      });
    };

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateViewport, 150);
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    fetchExhibitions();
  }, []);

  useEffect(() => {
    filterExhibitions();
  }, [exhibitions, itemsToShow, filter, searchQuery]);

  const fetchExhibitions = async () => {
    try {
      const response = await axios.get(`${API_URL}/exhibitions`);
      setExhibitions(response.data);
    } catch (error) {
      console.error('Error fetching exhibitions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterExhibitions = () => {
    let filtered = [...exhibitions];
    
    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(exhibition =>
        exhibition.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exhibition.organizer.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (exhibition.location && exhibition.location.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Apply date filter
    const currentDate = new Date();
    if (filter === 'recent') {
      filtered = filtered.filter(exhibition => {
        const exhibitionDate = new Date(exhibition.year, 0);
        const yearsDiff = currentDate.getFullYear() - exhibitionDate.getFullYear();
        return yearsDiff <= 2;
      });
    } else if (filter === 'past') {
      filtered = filtered.filter(exhibition => {
        const exhibitionDate = new Date(exhibition.year, 0);
        const yearsDiff = currentDate.getFullYear() - exhibitionDate.getFullYear();
        return yearsDiff > 2;
      });
    }
    
    setDisplayedExhibitions(filtered.slice(0, itemsToShow));
  };

  const handleShowMore = () => {
    setShowMoreLoading(true);
    setTimeout(() => {
      const increment = viewport.isMobile ? 5 : viewport.isTablet ? 8 : 10;
      setItemsToShow(prev => prev + increment);
      setShowMoreLoading(false);
    }, 500);
  };

  const getFilteredCount = () => {
    let filtered = [...exhibitions];
    
    if (searchQuery.trim()) {
      filtered = filtered.filter(exhibition =>
        exhibition.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exhibition.organizer.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (exhibition.location && exhibition.location.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    const currentDate = new Date();
    if (filter === 'recent') {
      filtered = filtered.filter(exhibition => {
        const exhibitionDate = new Date(exhibition.year, 0);
        const yearsDiff = currentDate.getFullYear() - exhibitionDate.getFullYear();
        return yearsDiff <= 2;
      });
    } else if (filter === 'past') {
      filtered = filtered.filter(exhibition => {
        const exhibitionDate = new Date(exhibition.year, 0);
        const yearsDiff = currentDate.getFullYear() - exhibitionDate.getFullYear();
        return yearsDiff > 2;
      });
    }
    
    return filtered.length;
  };

  const groupExhibitionsByYear = () => {
    const grouped = {};
    displayedExhibitions.forEach(exhibition => {
      if (!grouped[exhibition.year]) {
        grouped[exhibition.year] = [];
      }
      grouped[exhibition.year].push(exhibition);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="exhibition-loading">
        <div className="loading-spinner"></div>
        <p>Loading exhibitions...</p>
      </div>
    );
  }

  const groupedExhibitions = groupExhibitionsByYear();
  const years = Object.keys(groupedExhibitions).sort((a, b) => b - a);
  const totalFiltered = getFilteredCount();

  return (
    <section id="exhibitions" className="exhibition-section">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">
            Exhibition Journey
            <span className="title-accent">({totalFiltered} exhibitions)</span>
          </h2>
          <p className="section-subtitle">
            A chronological showcase of artistic presence and recognition across prestigious platforms
          </p>
        </div>

        {/* Filters */}
        {/* <div className="exhibition-filters">
          <div className="filter-group">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => {
                setFilter('all');
                setItemsToShow(10);
              }}
            >
              <TrendingUp size={16} />
              All Exhibitions
            </button>
            <button
              className={`filter-btn ${filter === 'recent' ? 'active' : ''}`}
              onClick={() => {
                setFilter('recent');
                setItemsToShow(10);
              }}
            >
              <Sparkles size={16} />
              Recent (2 years)
            </button>
            <button
              className={`filter-btn ${filter === 'past' ? 'active' : ''}`}
              onClick={() => {
                setFilter('past');
                setItemsToShow(10);
              }}
            >
              <Clock size={16} />
              Archive
            </button>
          </div>
          
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search exhibitions..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setItemsToShow(10);
              }}
            />
          </div>
        </div> */}

        {/* Timeline */}
        {displayedExhibitions.length > 0 ? (
          <div className="exhibition-timeline">
            {years.map((year, yearIndex) => (
              <div key={year} className="timeline-year-group">
                <div className="year-marker">
                  <div className="year-circle">
                    <Calendar size={24} />
                  </div>
                  <h3 className="year-label">{year}</h3>
                </div>

                <div className="timeline-items">
                  {groupedExhibitions[year].map((exhibition, index) => (
                    <div
                      key={exhibition.id}
                      className="timeline-item"
                      style={{
                        animationDelay: `${index * 0.1}s`
                      }}
                    >
                      <div className="timeline-connector"></div>
                      <div className="exhibition-card">
                        <div className="exhibition-badge">
                          <Award size={16} />
                        </div>
                        
                        <div className="exhibition-content">
                          <h4 className="exhibition-title">{exhibition.title}</h4>
                          
                          <div className="exhibition-details">
                            {exhibition.location && exhibition.location.trim() && (
                              <div className="detail-item">
                                <MapPin size={14} />
                                <span>{exhibition.location}</span>
                              </div>
                            )}
                            
                            <div className="detail-item">
                              <Award size={14} />
                              <span>{exhibition.organizer}</span>
                            </div>

                            {/* {exhibition.type && (
                              <div className="detail-item">
                                <Sparkles size={14} />
                                <span className="exhibition-type">{exhibition.type}</span>
                              </div>
                            )} */}
                          </div>

                          {exhibition.description && (
                            <p className="exhibition-description">
                              {exhibition.description}
                            </p>
                          )}

                          {exhibition.website && (
                            <a
                              href={exhibition.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="exhibition-link"
                            >
                              <span>View Details</span>
                              <ExternalLink size={14} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-exhibitions">
            <Award size={64} />
            <h3>No Exhibitions Found</h3>
            <p>Try adjusting your filters or search query</p>
          </div>
        )}

        {/* Show More Button */}
        {displayedExhibitions.length < totalFiltered && (
          <div className="show-more-container">
            <button
              className="show-more-btn"
              onClick={handleShowMore}
              disabled={showMoreLoading}
            >
              {showMoreLoading ? (
                <>
                  <div className="spinner"></div>
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <ChevronDown size={20} />
                  <span>Show More Exhibitions</span>
                </>
              )}
            </button>
            <p className="show-more-info">
              Showing {displayedExhibitions.length} of {totalFiltered}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Exhibition;